import { useStore } from "@/store";
import { IAdvizerModel } from "@/models/Advizer";
import {
  IAdvizeApiDataKeys,
  IAdvizerSearchFilterKeys,
} from "@/types/StoreTypes";
import { searchAdvizers } from "@/lib/advizerSearch";
import { User } from "@/models/User";
import { IPartner, IQuestion } from "@/types/ApiTypes";
import { config } from "@/config";
import { DEMO_PARTNERS } from "@/configs/partners";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { IAdvizerVideo } from "@/models/AdvizerVideo";
import { useIsDemoApp } from "./selectors/featureSelectors";
import { useUserRecommendedAdvizers } from "./selectors/userProfileSelectors";
/* Api Data */
export const useSetApiData = () => {
  return useStore((state) => state.advizeApiData.setApiData);
};

export const useApiData = (key: IAdvizeApiDataKeys) => {
  const advizeApiData = useStore((state) => state.advizeApiData[key]);
  return advizeApiData || null;
};

export const useIsApiDown = () => {
  const advizers = useStore((state) => state.advizeApiData.advizers);
  return advizers && !advizers?.length;
};

export const useAllQuestions = () => {
  const questions = useStore((state) => state.advizeApiData.questions);
  return questions;
};

export const useQuestions = () => {
  let questions = useAllQuestions();
  const activePartner = useActiveUserPartner();
  const isDemoApp = useIsDemoApp();

  questions = (questions || [])?.map((question) => {
    return question;
  });

  if (isDemoApp) {
    questions?.push({
      id: "custom-question-1",
      title: "Custom question for your alums!",
      number: 14,
      isStandard: false,
      // @ts-ignore
      isDemo: true,
    });
    questions?.push({
      id: "custom-question-2",
      title: "Another custom question!",
      number: 15,
      isStandard: false,
      // @ts-ignore
      isDemo: true,
    });
  }

  return (
    questions
      ?.filter(
        (question: IQuestion) =>
          // @ts-ignore
          question.isDemo ||
          question.isStandard ||
          question.partners?.find((p: IPartner) => p.id === activePartner?.id),
      )
      .map((question: IQuestion) => {
        return {
          ...question,
          questionPrefix: `Q${question.number === 13.1 ? "13B" : question.number}`,
        };
      })
      .sort((a: IQuestion, b: IQuestion) => {
        const aRank = (a.number || 0) + (a.isStandard ? 0 : -10000);
        const bRank = (b.number || 0) + (b.isStandard ? 0 : -10000);
        // sort not isStandard to the top, then by number
        return aRank - bRank;
      }) || []
  );
};

export const useCurrentPartnerHasSpecialQuestions = () => {
  const questions = useQuestions();
  return questions?.some((question: any) => !question.isStandard);
};

export const useActiveQuestion = () => {
  const activeQuestionId = useStore(
    (state) => state.advizerSearch.activeQuestionId,
  );
  const questions = useQuestions();
  return questions.find((question: any) => question.id === activeQuestionId);
};

/* Advizer Search */

let timeout: any;
let isSearchingTimeout: any;

export const useIsSearchingAdvizers = () => {
  const isSearchingAdvizers = useStore(
    (state) => state.advizerSearch.isSearchingAdvizers,
  );
  const setIsSearchingAdvizers = useStore(
    (state) => state.advizerSearch.setIsSearchingAdvizers,
  );
  return {
    isSearchingAdvizers,
    setIsSearchingAdvizers,
  };
};

// TODO make a map of visible advizers and return null if not visible, only look for the ID with a hook in each AdvizerVideoCard, return null if not
export const useFilteredAdvizers = (skipEffect = false) => {
  const { advizers, isAdvizersLoading } = useAdvizers();
  const [filteredAdvizers, setFilteredAdvizers] = useState<
    IAdvizerModel[] | null
  >(null);
  const { isSearchingAdvizers, setIsSearchingAdvizers } =
    useIsSearchingAdvizers();
  const { searchText } = useAdvizerSearchText();
  const { subjects, industries, jobFunctions, traits, companies } =
    useSelectedAdvizerSearchFilters();
  const recommendedAdvizerData = useUserRecommendedAdvizers();
  const advizerMap = useAdvizerMap();

  const activeSearchGradDegree = useStore(
    (state) => state.advizerSearch.activeSearchGradDegree,
  );
  const activeQuestionNumber = useStore(
    (state) => state.advizerSearch.activeQuestionNumber,
  );

  const activeQuestionId = useStore(
    (state) => state.advizerSearch.activeQuestionId,
  );

  const activeFilterPartnerId = useStore(
    (state) => state.advizerSearch.activeFilterPartnerId,
  );

  const recommendedAdvizers = useMemo(() => {
    if (!recommendedAdvizerData) return [];
    return Object.keys(recommendedAdvizerData)
      .filter((id) => recommendedAdvizerData[id])
      .map((id) => advizerMap[id])
      .filter((advizer) => !!advizer);
  }, [recommendedAdvizerData, advizerMap]);

  // Create a stable dependency key from recommended advizer IDs
  const recommendedAdvizerIdsKey = useMemo(() => {
    if (!recommendedAdvizerData) return "";
    return Object.keys(recommendedAdvizerData)
      .filter((id) => recommendedAdvizerData[id])
      .sort()
      .join(",");
  }, [recommendedAdvizerData]);

  useEffect(() => {
    if (!advizers || skipEffect) return;

    setIsSearchingAdvizers(true);

    timeout = setTimeout(() => {
      const filtered = searchAdvizers({
        advizers: [...recommendedAdvizers, ...(advizers || [])],
        searchText,
        selectedSubjects: subjects,
        selectedIndustries: industries,
        selectedJobFunctions: jobFunctions,
        selectedTraits: traits,
        selectedCompanies: companies,
        partnerId: activeFilterPartnerId,
        activeQuestionId,
        activeSearchGradDegree,
      });

      setFilteredAdvizers(filtered);

      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
      }, 100);

      isSearchingTimeout = setTimeout(() => {
        setIsSearchingAdvizers(false);
      }, 200);
    }, 300);

    return () => {
      clearTimeout(timeout);
      clearTimeout(isSearchingTimeout);
    };
    // recommendedAdvizers is intentionally excluded from dependencies - we use recommendedAdvizerIdsKey instead
    // to avoid infinite re-renders caused by array reference changes
  }, [
    searchText,
    subjects,
    industries,
    advizers,
    jobFunctions,
    traits,
    companies,
    skipEffect,
    activeFilterPartnerId,
    activeQuestionId,
    setIsSearchingAdvizers,
    activeSearchGradDegree,
    activeQuestionNumber,
    recommendedAdvizerIdsKey,
  ]);

  return {
    advizers: filteredAdvizers || advizers,
    isShowingAllAdvizers: filteredAdvizers?.length === advizers?.length,
    isAdvizersLoading,
    isSearchingAdvizers,
  };
};

export const useActivePartnerAdvizerCount = () => {
  const activePartner = useActiveUserPartner();
  const { advizers } = useAdvizers();
  return (
    advizers?.filter((advizer) =>
      advizer?.partners?.some((p) => p.id === activePartner?.id),
    )?.length || 0
  );
};

export const useFilteredAdvizersCount = () => {
  const filteredAdvizers = useFilteredAdvizers();
  return filteredAdvizers?.advizers?.length || 0;
};

export const useSelectedSubjects = () => {
  const subjects = useStore((state) => state.advizerSearch.subjects);
  return subjects;
};

export const useSelectedAdvizerSearchFilters = () => {
  const subjects = useStore((state) => state.advizerSearch.subjects);
  const industries = useStore((state) => state.advizerSearch.industries);
  const jobFunctions = useStore((state) => state.advizerSearch.jobFunctions);
  const traits = useStore((state) => state.advizerSearch.traits);
  const companies = useStore((state) => state.advizerSearch.companies);
  const searchText = useStore((state) => state.advizerSearch.searchText);
  const activeQuestionId = useStore(
    (state) => state.advizerSearch.activeQuestionId,
  );
  const activeFilterPartnerId = useStore(
    (state) => state.advizerSearch.activeFilterPartnerId,
  );

  return {
    subjects,
    industries,
    jobFunctions,
    traits,
    companies,
    searchText,
    activeQuestionId,
    activeFilterPartnerId,
  };
};

export const useCheckIsFilterOptionSelected = ({
  filterKey,
  resourceId,
}: {
  filterKey: IAdvizerSearchFilterKeys;
  resourceId: string;
}) => {
  return useStore((state) => !!state.advizerSearch?.[filterKey]?.[resourceId]);
};

export const useToggleAdvizerSearchFilterOption = () => {
  return useStore((state) => state.advizerSearch.toggleFilterOption);
};

export const useIsFilterOptionSelected = ({
  filterKey,
  resourceId,
}: {
  filterKey: IAdvizerSearchFilterKeys;
  resourceId: string;
}) => {
  return useStore((state) => state.advizerSearch?.[filterKey]?.[resourceId]);
  // if (!filterKey || !resourceId) return false;

  // return checkIsFilterOptionSelected(filterKey, resourceId);
};

export const useAdvizerSearchText = () => {
  const searchText = useStore((state) => state.advizerSearch.searchText);
  const setSearchText = useStore((state) => state.advizerSearch.setSearchText);
  return {
    searchText,
    setSearchText,
  };
};

export const useIsAdvizersFiltered = () => {
  const { searchText } = useAdvizerSearchText();
  const { subjects, industries, jobFunctions, traits, companies } =
    useSelectedAdvizerSearchFilters();
  const activeQuestionId = useStore(
    (state) => state.advizerSearch.activeQuestionId,
  );

  return !(
    searchText.length === 0 &&
    Object.values(subjects).length === 0 &&
    Object.values(industries).length === 0 &&
    Object.values(jobFunctions).length === 0 &&
    Object.values(traits).length === 0 &&
    Object.values(companies).length === 0 &&
    !activeQuestionId
  );
};

export const useAdvizersForActivePartnerFilter = () => {
  const activeFilterPartnerId = useStore(
    (state) => state.advizerSearch.activeFilterPartnerId,
  );
  const { advizers } = useAdvizers();
  return searchAdvizers({
    advizers: advizers || [],
    partnerId: activeFilterPartnerId,
  });
};

export const useSubjectFilterOptions = () => {
  let subjects = useStore((state) => state.advizeApiData.subjects);
  const activeFilterPartnerId = useStore(
    (state) => state.advizerSearch.activeFilterPartnerId,
  );
  const advizersForActivePartnerFilter = useAdvizersForActivePartnerFilter();
  const { advizers: allAdvizers } = useAdvizers();

  if (activeFilterPartnerId) {
    subjects = (subjects || []).filter((subject) =>
      advizersForActivePartnerFilter.some((advizer) =>
        advizer.subjects?.find((s) => s.id === subject.id),
      ),
    );
  }

  if (config.isAdobeStudentApp) {
    subjects = (subjects || []).filter((subject) =>
      allAdvizers.some((advizer) =>
        advizer.subjects?.some((s) => s.id === subject.id),
      ),
    );
  }

  return {
    subjects: subjects
      ? [...subjects].sort((a, b) => a.title.localeCompare(b.title))
      : [],
    isSubjectsLoading: !subjects,
  };
};

export const useJobFunctionFilterOptions = () => {
  let jobFunctions = useStore((state) => state.advizeApiData.jobFunctions);
  const { advizers } = useAdvizers();

  if (config.isAdobeStudentApp) {
    jobFunctions = (jobFunctions || []).filter((jf) =>
      advizers.some((advizer) =>
        advizer.jobFunctions?.some((ajf) => ajf.id === jf.id),
      ),
    );
  }

  return {
    jobFunctions: jobFunctions
      ? [...jobFunctions].sort((a, b) => a.title.localeCompare(b.title))
      : [],
    isJobFunctionsLoading: !jobFunctions,
  };
};

export const useIndustryFilterOptions = () => {
  let industries = useStore((state) => state.advizeApiData.industries);
  const { advizers } = useAdvizers();

  if (config.isAdobeStudentApp) {
    industries = (industries || []).filter((ind) =>
      advizers.some((advizer) =>
        advizer.industries?.some((ai) => ai.id === ind.id),
      ),
    );
  }

  return {
    industries: industries
      ? [...industries].sort((a, b) => a.title.localeCompare(b.title))
      : [],
    isIndustriesLoading: !industries,
  };
};

export const useCompanyFilterOptions = () => {
  const { advizers } = useAdvizers();
  const companies = useMemo(() => {
    return [...new Set((advizers || []).map((advizer) => advizer.company))]
      .filter((company) => !!company)
      .sort((a, b) => a?.localeCompare(b));
  }, [advizers]);

  return {
    companies: companies,
    isCompaniesLoading: false,
  };
};

export const useTraitFilterOptions = () => {
  let traits = useStore((state) => state.advizeApiData.traits);
  const { advizers } = useAdvizers();

  if (config.isAdobeStudentApp) {
    traits = (traits || []).filter((trait) =>
      advizers.some((advizer) =>
        advizer.traits?.some((at) => at.id === trait.id),
      ),
    );
  }

  return {
    traits: traits
      ? [...traits].sort((a, b) => a.title.localeCompare(b.title))
      : [],
    isTraitsLoading: !traits,
  };
};

export const useArrayOfSelectedAdvizerSearchFilters = () => {
  const { traits: traitOptions } = useTraitFilterOptions();
  const { companies: companyOptions } = useCompanyFilterOptions();
  const { industries: industryOptions } = useIndustryFilterOptions();
  const { jobFunctions: jobFunctionOptions } = useJobFunctionFilterOptions();
  const { subjects: subjectOptions } = useSubjectFilterOptions();

  function makeOptionLookupMap(options: any[]) {
    return options.reduce(
      (acc, item) => {
        acc[item?.id || item] = item;
        return acc;
      },
      {} as Record<string, any>,
    );
  }

  const optionLookupMap = {
    traits: makeOptionLookupMap(traitOptions),
    companies: makeOptionLookupMap(companyOptions),
    industries: makeOptionLookupMap(industryOptions),
    jobFunctions: makeOptionLookupMap(jobFunctionOptions),
    subjects: makeOptionLookupMap(subjectOptions),
  };

  const { companies, industries, jobFunctions, subjects, traits } =
    useSelectedAdvizerSearchFilters();

  function makeArrayOfSelectedFilters(
    filterMap: Record<string, boolean>,
    type: "companies" | "industries" | "jobFunctions" | "subjects" | "traits",
  ) {
    return Object.keys(filterMap)
      .filter((key) => filterMap[key])
      .map((key) => {
        const item = optionLookupMap[type][key];
        return {
          id: key,
          type,
          title: item?.title || item,
        };
      });
  }

  const selectedFilters = {
    companies: makeArrayOfSelectedFilters(companies, "companies"),
    industries: makeArrayOfSelectedFilters(industries, "industries"),
    jobFunctions: makeArrayOfSelectedFilters(jobFunctions, "jobFunctions"),
    subjects: makeArrayOfSelectedFilters(subjects, "subjects"),
    traits: makeArrayOfSelectedFilters(traits, "traits"),
  };

  return Object.values(selectedFilters).flat();
};

export const useShowPartnerToggle = () => {
  const partner = useActiveUserPartner();
  const isDemoApp = useIsDemoApp();
  const { advizers } = useAdvizers();

  if (isDemoApp) return true;

  if (!partner) return false;

  const totalAdvizersWithPartner = (advizers || [])?.filter((advizer) =>
    advizer?.partners?.some((p: any) => p?.id === partner?.id),
  )?.length;

  return !!partner && totalAdvizersWithPartner >= 5;
};

/* Advizers */

export const useApiAdvizers = () => {
  const advizers = useStore((state) => state.advizeApiData.advizers);
  return advizers;
};

export const useApiQuotes = () => {
  return useStore((state) => state.advizeApiData.quotes);
};

export const useApiQuotesAdvizerMap = () => {
  const apiQuotes = useApiQuotes();
  const map = useMemo(() => {
    const map: Record<string, any[]> = {};
    apiQuotes?.forEach((quote: any) => {
      if (!map[quote.advizerId]) {
        map[quote.advizerId] = [];
      }
      map[quote.advizerId].push(quote);
    });
    return map;
  }, [apiQuotes]);

  return map;
};

export const useAdvizerQuotes = (advizerId: string) => {
  const apiQuotesAdvizerMap = useApiQuotesAdvizerMap();
  return apiQuotesAdvizerMap[advizerId] || [];
};

export const useApiDescriptors = () => {
  return useStore((state) => state.advizeApiData.descriptors);
};

export const useApiDescriptorsVideoMap = () => {
  const apiDescriptors = useApiDescriptors();
  const map = useMemo(() => {
    const map: Record<string, any[]> = {};
    apiDescriptors?.forEach((descriptor) => {
      if (!descriptor.videoId) return;
      if (!map[descriptor.videoId]) {
        map[descriptor.videoId] = [];
      }
      map[descriptor.videoId].push(descriptor);
    });
    return map;
  }, [apiDescriptors]);
  return map;
};

export const useDescriptorsForVideo = (videoId: string) => {
  const apiDescriptorsVideoMap = useApiDescriptorsVideoMap();
  const descriptors = apiDescriptorsVideoMap[videoId] || [];
  return (
    descriptors
      // .filter((d) => {
      //   // filter out duplicates
      //   return !descriptors.some((d2) => d2.id !== d.id && d2.text === d.text);
      // })
      .slice(0, 3)
  );
};

export const useAdvizers = () => {
  const apiAdvizers = useApiAdvizers();
  const userPartner = useActiveUserPartner();
  const [mostRecentAdvizers, setMostRecentAdvizers] = useState<IAdvizerModel[]>(
    [],
  );

  const filteredAdvizers = useMemo(() => {
    if (!apiAdvizers) return [];

    return apiAdvizers.filter((advizer) => {
      if (config.isAdobeStudentApp) {
        return (advizer.partners || []).some(
          (p) => p.slug === config.partnerSubdomain,
        );
      }

      if (advizer.partnerStudentsOnly) {
        if (!userPartner) return false;
        if ((advizer.partners || [])?.some((p) => p.id === userPartner.id))
          return true;
        return false;
      }

      return true;
    });
  }, [apiAdvizers, userPartner]);

  useEffect(() => {
    if (filteredAdvizers?.length !== mostRecentAdvizers?.length) {
      setMostRecentAdvizers(filteredAdvizers);
    }
  }, [filteredAdvizers, mostRecentAdvizers]);

  return {
    advizers: mostRecentAdvizers as IAdvizerModel[],
    isAdvizersLoading: !apiAdvizers?.length,
  };
};

export const useAdvizerMap = () => {
  const { advizers } = useAdvizers();
  const advizerMap = useMemo(() => {
    return (advizers || []).reduce(
      (acc, advizer) => {
        acc[advizer.id] = advizer;
        return acc;
      },
      {} as Record<string, IAdvizerModel>,
    );
  }, [advizers]);
  return advizerMap;
};

export const useAdvizer = (advizerId: string) => {
  const advizerMap = useAdvizerMap();
  const advizerRaw = advizerMap[advizerId];

  if (!advizerRaw) {
    return null;
  }

  return advizerRaw;

  // return new Advizer(advizerRaw);
};

export const useRandomAdvizer = () => {
  const { advizers } = useAdvizers();
  const randomAdvizer = useMemo(() => {
    if (!advizers?.length) return null;
    return advizers[Math.floor(Math.random() * advizers?.length)];
  }, [advizers]);
  return randomAdvizer;
};

/* Ui Slice */

export const useUiSlice = () => {
  const store = useStore();
  return store;
};

/* Drawer */

export const useDrawer = (drawerId?: string) => {
  const activeDrawerId = useStore((state) => state.drawers.activeDrawerId);
  const setActiveDrawer = useStore((state) => state.drawers.setActiveDrawer);

  return {
    activeDrawerId,
    isOpen: activeDrawerId === drawerId,
    openDrawer: () => setActiveDrawer(drawerId || null),
    closeDrawer: () => setActiveDrawer(null),
    closeActiveDrawer: () => setActiveDrawer(null),
  };
};

export const useCloseActiveDrawer = () => {
  const { closeActiveDrawer } = useDrawer();
  return closeActiveDrawer;
};

/* Dialogs */
type IDialogId = "welcome" | "cookieConsent";
export const useDialog = (dialogId?: IDialogId) => {
  const activeDialogId = useStore((state) => state.dialog.activeDialogId);
  const setActiveDialog = useStore((state) => state.dialog.setActiveDialog);

  return {
    activeDialogId,
    isOpen: activeDialogId === dialogId,
    openDialog: () => setActiveDialog(dialogId || null),
    closeDialog: () => setActiveDialog(null),
    closeActiveDialog: () => setActiveDialog(null),
  };
};

/* Videos */

export const useIsVideoPlaying = (videoId: string) => {
  const activeVideoId = useStore((state) => state.videos.activeVideoId);
  return activeVideoId === videoId;
};

export const useIsAnyVideoPlaying = () => {
  const activeVideoId = useStore((state) => state.videos.activeVideoId);
  return !!activeVideoId;
};

export const useVimeoIdVideoMap = () => {
  const { advizers } = useAdvizers();
  const map = useMemo(() => {
    const map: Record<string, IAdvizerVideo> = {};
    (advizers || [])?.forEach((advizer: IAdvizerModel) => {
      advizer.videos.forEach((video: IAdvizerVideo) => {
        if (!video.videoVimeoId) return;
        if (!map[video.videoVimeoId]) {
          map[video.videoVimeoId] = video;
        }
      });
    });
    return map;
  }, [advizers]);

  return map;
};

export const useAdvizerQuestionVideoMap = () => {
  const { advizers } = useAdvizers();
  const map = useMemo(() => {
    const map: Record<string, Record<string, IAdvizerVideo>> = {};
    (advizers || []).forEach((advizer: IAdvizerModel) => {
      advizer.videos.forEach((video: IAdvizerVideo) => {
        if (!map[advizer.id]) {
          map[advizer.id] = {};
        }
        if (video.questionNumber && !map[advizer.id][video.questionNumber]) {
          map[advizer.id][video.questionNumber] = video;
        }
      });
    });
    return map;
  }, [advizers]);

  return map;
};

/* User */

export const useActiveUser = () => {
  const user = useStore((state) => state.user.activeUser);
  return user ? new User(user) : null;
};

export const useIsLoggedIn = () => {
  const user = useActiveUser();
  return !!user;
};

export const useIsAdmin = () => {
  return useStore((state) => !!state.user.activeUser?.isAdmin);
};

export const useIsRestrictedApp = () => {
  const isLoggedIn = useIsLoggedIn();
  const isDemoApp = useIsDemoApp();
  const numVideosPlayed = useStore((state) => state.user.numVideosPlayed);
  if (isDemoApp) return false;
  return !isLoggedIn && numVideosPlayed > 0;
};

export const useUIFlags = () => {
  const activeUser = useActiveUser();
  const behavior = activeUser?.behavior || {};

  const highlightSignInButton = !activeUser;
  const highlightAdvizerFilterButton =
    activeUser && !behavior?.didClickAdvizerFilter;
  const highlightQuestionSelector =
    activeUser && !highlightAdvizerFilterButton && !behavior?.didSelectQuestion;
  const highlightAdvizerTags = activeUser && !behavior?.didClickAdvizerTags;

  return {
    highlightSignInButton,
    highlightAdvizerFilterButton,
    highlightQuestionSelector,
    highlightAdvizerTags,
  };
};

export const useIsUserProfile = () => {
  const pathname = useLocation().pathname;
  return pathname.includes("profile");
};

/* Partners */

export function getVisiblePartners(partners: IPartner[]) {
  const slugMatchesDemo = (
    dbSlug: string,
    demo: (typeof DEMO_PARTNERS)[number],
  ) => demo.slug === dbSlug || (demo.aliasSlugs?.includes(dbSlug) ?? false);

  const findDemoConfig = (dbSlug: string) =>
    DEMO_PARTNERS.find((d) => slugMatchesDemo(dbSlug, d));

  const filteredPartners = partners?.filter((p) => {
    const cfg = findDemoConfig(p.slug);
    if (!cfg) return true;
    return config.isProductionClient ? cfg.allowInProduction : true;
  });

  const merged = filteredPartners.map((partner) => {
    const cfg = findDemoConfig(partner.slug);
    if (!cfg) return partner;
    return { ...partner, ...cfg, slug: cfg.slug };
  });

  const demoPartnersOnly = DEMO_PARTNERS.filter(
    (p) =>
      !!p.title &&
      !partners?.some((partner) => slugMatchesDemo(partner.slug, p)) &&
      (config.isProductionClient ? p.allowInProduction : true),
  );

  return [...merged, ...demoPartnersOnly];
}

export const usePartners = () => {
  const partners = useStore((state) => state.advizeApiData.partners);
  return getVisiblePartners(partners || []);
};

export const useIsPartnersLoading = () => {
  const partners = useStore((state) => state.advizeApiData.partners);
  return !partners;
};

export const usePartner = (partnerId?: string) => {
  const partners = usePartners();

  if (!partnerId) return null;

  return partners?.find((partner) => partner.id === partnerId);
};

export const usePartnerById = (partnerId?: string) => {
  const partners = usePartners();
  return partners?.find((partner) => partner.id === partnerId);
};

export const usePartnerBySlug = (partnerSlug?: string) => {
  const partners = usePartners();
  const activePartner = useStore((state) => state.partner.activePartner);
  if (!partnerSlug) return null;
  if (activePartner?.slug === partnerSlug) return activePartner;
  return partners?.find((partner: any) => partner.slug === partnerSlug);
};

export const useActiveUserPartner = () => {
  const activePartner = useStore((state) => state.partner.activePartner);
  const partners = usePartners();

  try {
    const localPartner = localStorage.getItem("activePartner");

    if (localPartner) {
      return (
        partners?.find((partner) => partner.slug.includes(localPartner)) ||
        activePartner
      );
    }
  } catch (e) {
    console.error(e);
    return activePartner;
  }

  return activePartner;
};

export const useDoesUserHaveAnyPartners = () => {
  const activePartner = useActiveUserPartner();
  return !!activePartner;
};

export const usePartnerFromUrl = () => {
  const [searchParams] = useSearchParams();
  const partnerSlugSearchParam = searchParams.get("partner");
  const partners = usePartners();
  return partners?.find((partner) => partner.slug === partnerSlugSearchParam);
};
